"use client"

import type { FC, HTMLAttributes, MouseEventHandler, ReactNode } from "react"
import { Link as AriaLink } from "react-aria-components"

import { cx } from "@/components/ui/cx"

/*
 * A rail item on the print. Text is the on-print ladder; the selected item is a
 * sheet of paper pinned to the board — stone.50 fill, ink text, shadow.lift.
 * The right-hand slot carries a mono count, never a coloured badge.
 */
export const NavItem = ({
  current,
  href,
  icon: Icon,
  count,
  children,
  onClick,
}: {
  href: string
  icon?: FC<HTMLAttributes<HTMLOrSVGElement>>
  count?: ReactNode
  current?: boolean
  onClick?: MouseEventHandler
  children: ReactNode
}) => {
  const isExternal = href.startsWith("http")
  return (
    <AriaLink
      href={href}
      target={isExternal ? "_blank" : "_self"}
      rel="noopener noreferrer"
      onClick={onClick}
      aria-current={current ? "page" : undefined}
      className={cx(
        "group flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 text-[13px] leading-none font-medium select-none",
        "outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stone-50",
        "transition-[background-color,color] duration-(--duration-snap)",
        current
          ? "shadow-lift text-ink-950 bg-stone-50 font-semibold"
          : "text-onprint-emphasis hover:bg-onprint-hover hover:text-stone-50",
      )}
    >
      {Icon && (
        <Icon
          aria-hidden="true"
          className={cx(
            "size-[15px] shrink-0 stroke-[1.6]",
            current ? "text-ink-nontext" : "text-onprint-quiet",
          )}
        />
      )}
      <span className="flex-1 truncate">{children}</span>
      {count !== undefined && count !== null && (
        <span
          className={cx(
            "text-2xs font-mono leading-none font-medium tabular-nums",
            current ? "text-ink-quiet" : "text-onprint-quiet",
          )}
        >
          {count}
        </span>
      )}
    </AriaLink>
  )
}
